import React from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, RotateCcw, X, Flame, Palmtree, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { useOrder } from "../context/OrderContext";
import { formatEuro, computeTotals, getLastOrder, clearLastOrder } from "../lib/menu";
import { Seo } from "../components/Seo";
import { ShareButton } from "../components/ShareButton";
import { track } from "../lib/meta";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STEP = 1;
const LOW_STOCK = 10;

const fmtCountdown = (s) => {
  if (s <= 0) return "";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}u ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

const DeadlineCountdown = ({ seconds, label, testKey }) => {
  const [left, setLeft] = React.useState(seconds);
  React.useEffect(() => { setLeft(seconds); }, [seconds]);
  React.useEffect(() => {
    if (left == null || left <= 0) return;
    const t = setInterval(() => setLeft((v) => (v == null ? v : v - 1)), 1000);
    return () => clearInterval(t);
  }, [left == null]);
  if (left == null || left <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-[700] text-[#B71C1C] bg-[#B71C1C]/10 px-2.5 py-1 rounded-full" data-testid={`cat-countdown-${testKey}`}>
      <Clock size={13} /> nog {fmtCountdown(left)} tot de {label}-deadline
    </span>
  );
};

const dishImgSrc = (p) => (p.startsWith("http") ? p : `${BACKEND}${p}`);

const DishImages = ({ images, alt, soldOut }) => {
  const [idx, setIdx] = React.useState(0);
  const startX = React.useRef(null);
  const list = images && images.length ? images : [];
  const go = (i) => setIdx((i + list.length) % list.length);
  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null || list.length < 2) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) go(idx + (dx < 0 ? 1 : -1));
    startX.current = null;
  };
  return (
    <div className="aspect-[4/3] overflow-hidden bg-[#E8F5E9] relative" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid={list.length > 1 ? "dish-carousel" : "dish-image"}>
      {list.length > 0 && (
        <div className="flex h-full transition-transform duration-500 ease-out" style={{ transform: `translateX(-${idx * 100}%)` }}>
          {list.map((p, i) => (
            <img key={i} src={dishImgSrc(p)} alt={`${alt} foto ${i + 1}`} loading="lazy" decoding="async" className={`w-full h-full object-cover flex-shrink-0 ${soldOut ? "grayscale opacity-60" : ""}`} />
          ))}
        </div>
      )}
      {list.length > 1 && !soldOut && (
        <>
          <button onClick={() => go(idx - 1)} aria-label="Vorige foto" data-testid="dish-carousel-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center text-[#7A5230] hover:bg-white transition-colors">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => go(idx + 1)} aria-label="Volgende foto" data-testid="dish-carousel-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center text-[#7A5230] hover:bg-white transition-colors">
            <ChevronRight size={18} />
          </button>
        </>
      )}
      {list.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
          {list.map((_, i) => (
            <button key={i} onClick={() => go(i)} aria-label={`Foto ${i + 1}`} data-testid={`dish-carousel-dot-${i}`}
              className={`w-2 h-2 rounded-full transition-colors ${i === idx ? "bg-white" : "bg-white/50"}`} />
          ))}
        </div>
      )}
      {soldOut && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/45">
          <span className="bg-[#B71C1C] text-white font-[700] uppercase tracking-wider text-sm px-4 py-2">Uitverkocht</span>
        </div>
      )}
    </div>
  );
};

export default function MenuBuilder() {
  const navigate = useNavigate();
  const { order, update, setQuantity, menu, menuLoading, menuError, refreshMenu, config } = useOrder();
  const { quantities, pickupDate, timeSlot, categoryPickups = {} } = order;

  const pickupDates = config?.pickup_dates || [];
  const selectedDateObj = pickupDates.find((d) => d.date === pickupDate);
  const timeSlots = selectedDateObj?.slots || [];
  const holiday = config?.holiday;

  const [lastOrder, setLastOrder] = React.useState(getLastOrder);

  React.useEffect(() => {
    if (!menuLoading && menu.length > 0) {
      track("ViewContent", {
        content_type: "product_group",
        content_ids: menu.map((d) => d.id),
        content_name: "Menu",
        currency: "EUR",
      });
    }
  }, [menuLoading, menu]);

  const { total, vat, totalPortions } = computeTotals(quantities, menu);

  const selected = menu.filter((d) => (quantities[d.id] || 0) > 0);

  // Per-category pickup requirements
  const menuCats = config?.menu_categories || [];
  const selectedCatKeys = [...new Set(selected.map((d) => d.category || "dinner"))];
  const scheduledCats = menuCats.filter((c) => c.has_schedule && selectedCatKeys.includes(c.key));
  const hasGlobal = selectedCatKeys.some((k) => {
    const c = menuCats.find((x) => x.key === k);
    return !c || !c.has_schedule;
  });
  const setCatPickup = (key, patch) =>
    update({ categoryPickups: { ...categoryPickups, [key]: { ...(categoryPickups[key] || {}), ...patch } } });
  const scheduledOk = scheduledCats.every((c) => categoryPickups[c.key]?.date && categoryPickups[c.key]?.slot);
  const globalOk = !hasGlobal || (pickupDate && timeSlot);
  const catPortionMap = {};
  selected.forEach((d) => { const k = d.category || "dinner"; catPortionMap[k] = (catPortionMap[k] || 0) + (quantities[d.id] || 0); });
  const unmetCats = menuCats.filter((c) => c.min_order > 0 && (catPortionMap[c.key] || 0) > 0 && catPortionMap[c.key] < c.min_order);
  const canProceed = selected.length > 0 && scheduledOk && globalOk && unmetCats.length === 0;

  const showRepeat = lastOrder && selected.length === 0;

  const applyLastOrder = () => {
    // Only re-apply dishes that still exist and clamp to today's availability.
    // Pickup date/slot are NOT reapplied (old dates may no longer be valid).
    const clamped = {};
    menu.forEach((d) => {
      const wanted = lastOrder.quantities[d.id] || 0;
      if (wanted > 0 && !d.sold_out) clamped[d.id] = Math.min(wanted, d.availability);
    });
    update({ quantities: clamped });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const dismissRepeat = () => {
    clearLastOrder();
    setLastOrder(null);
  };

  const goToCheckout = () => {
    navigate("/checkout");
    // Ensure the checkout opens at the very top with a smooth transition.
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const inc = (id) => {
    const d = menu.find((m) => m.id === id);
    const max = d ? d.availability : 0;
    const next = Math.min(max, (quantities[id] || 0) + STEP);
    if (d && next > (quantities[id] || 0)) {
      track("AddToCart", {
        content_type: "product",
        content_ids: [d.id],
        content_name: d.name,
        value: d.price,
        currency: "EUR",
      });
    }
    setQuantity(id, next);
  };
  const dec = (id) => setQuantity(id, Math.max(0, (quantities[id] || 0) - STEP));

  // ---- Category grouping ----
  const catMeta = config?.menu_categories || [];
  const grouped = catMeta
    .map((c) => ({ ...c, items: menu.filter((d) => (d.category || "dinner") === c.key) }))
    .filter((c) => c.items.length > 0);
  const groupedKeys = grouped.map((c) => c.key).join(",");
  const [activeCat, setActiveCat] = React.useState("");

  React.useEffect(() => {
    if (!groupedKeys) return;
    const keys = groupedKeys.split(",");
    const onScroll = () => {
      let current = keys[0];
      for (const key of keys) {
        const el = document.getElementById(`cat-${key}`);
        if (el && el.getBoundingClientRect().top - 130 <= 0) current = key;
      }
      setActiveCat(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [groupedKeys]);

  const scrollToCat = (key) => {
    const el = document.getElementById(`cat-${key}`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: "smooth" });
  };

  const renderDish = (d, catSoldOut) => {
    const qty = quantities[d.id] || 0;
    const soldOut = d.sold_out || catSoldOut;
    return (
      <div key={d.id} className={`border-2 border-[#E8F5E9] bg-white flex flex-col transition-opacity ${soldOut ? "opacity-70" : ""}`} data-testid={`dish-card-${d.id}`}>
        <DishImages images={d.images} alt={d.name} soldOut={soldOut} />
        {soldOut && <span className="hidden" data-testid={`sold-out-${d.id}`}>Uitverkocht</span>}
        <div className="p-5 flex flex-col gap-2 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-lg md:text-xl font-[600] text-[#0D3B4C]">{d.name}</h3>
            <span className="text-xl font-[700] text-[#B71C1C] whitespace-nowrap">{formatEuro(d.price)}</span>
          </div>
          <p className="text-sm font-[400] text-[#0D3B4C] flex-1">{d.desc}</p>
          {!soldOut && d.availability <= LOW_STOCK && (
            <p className="flex items-center gap-1.5 text-sm font-[700] text-[#B71C1C]" data-testid={`low-stock-${d.id}`}>
              <Flame size={16} className="fill-[#B71C1C]" /> Nog maar {d.availability} porties!
            </p>
          )}
          {soldOut ? (
            <p className="text-center font-[700] text-[#B71C1C] mt-3" data-testid={`sold-out-text-${d.id}`}>
              Uitverkocht! Binnenkort weer.
            </p>
          ) : (
            <div className="flex items-center justify-center gap-4 mt-3">
              <button onClick={() => dec(d.id)} data-testid={`stepper-dec-${d.id}`}
                className="w-11 h-11 rounded-full bg-[#1B6B2E] text-white flex items-center justify-center hover:bg-[#144F22] transition-colors disabled:opacity-40"
                disabled={qty === 0} aria-label={`Minder ${d.name}`}>
                <Minus size={20} />
              </button>
              <span className="w-14 text-center font-[700] text-2xl text-[#0D3B4C]" data-testid={`qty-${d.id}`}>{qty}</span>
              <button onClick={() => inc(d.id)} data-testid={`stepper-inc-${d.id}`}
                className="w-11 h-11 rounded-full bg-[#1B6B2E] text-white flex items-center justify-center hover:bg-[#144F22] transition-colors disabled:opacity-40"
                disabled={qty >= d.availability} aria-label={`Meer ${d.name}`}>
                <Plus size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-[1200px] mx-auto w-full px-6 py-12">
      <Seo
        title="Bestel online | De Zeeuwse Tori"
        description="Stel je menu samen en haal vers bereide Surinaamse gerechten op in Middelburg. Al vanaf 2 porties."
        image="og-bestellen.jpg"
        path="/menu-samenstellen"
      />
      <div className="text-center mb-10">
        <h1 className="text-4xl sm:text-5xl font-[700] text-[#0D3B4C] tracking-tight">Kies je gerecht(en)</h1>
        <p className="mt-3 text-lg md:text-xl font-[600] text-[#0D3B4C]">
          Ophalen vrijdag t/m zondag
        </p>
        <p className="mt-1 text-sm font-[400] text-[#0D3B4C]">
          Al onze maaltijden worden per portie verpakt.
        </p>
        <ShareButton slug="bestellen" title="Bestel online | De Zeeuwse Tori" label="Deel deze pagina" className="mt-4 inline-flex items-center gap-2 text-[#1B6B2E] font-[700] text-sm uppercase tracking-wider hover:text-[#0D3B4C] transition-colors" />
      </div>

      {holiday?.active && (
        <div className="mb-8 border-2 border-[#B71C1C] bg-[#FEF9E7] p-5 sm:p-6 flex items-start gap-4" data-testid="holiday-banner">
          <div className="w-11 h-11 rounded-full bg-[#B71C1C] text-white flex items-center justify-center flex-shrink-0">
            <Palmtree size={22} />
          </div>
          <div>
            <h3 className="font-[700] text-[#0D3B4C]">We zijn er even tussenuit</h3>
            <p className="text-sm font-[600] text-[#0D3B4C] mt-1">
              We zijn er even tussenuit — je kunt weer bestellen vanaf {holiday.resume_label || holiday.resume_date}.
            </p>
          </div>
        </div>
      )}

      {showRepeat && (
        <div className="mb-8 border-2 border-[#1B6B2E] bg-[#E8F5E9] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="repeat-order-card">
          <div className="w-12 h-12 rounded-full bg-[#1B6B2E] text-white flex items-center justify-center flex-shrink-0">
            <RotateCcw size={22} />
          </div>
          <div className="flex-1">
            <h3 className="font-[700] text-[#0D3B4C]">Welkom terug! Bestel je vorige menu opnieuw</h3>
            <p className="text-sm font-[400] text-[#0D3B4C] mt-1" data-testid="repeat-order-summary">
              {lastOrder.items.map((i) => `${i.name} × ${i.portions}`).join(" · ")}
              {" — "}<span className="font-[700]">{formatEuro(lastOrder.total)}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={applyLastOrder}
              data-testid="repeat-order-apply"
              className="bg-[#B71C1C] text-white font-[700] uppercase tracking-wider px-6 py-3 hover:bg-[#8E1515] transition-colors"
            >
              Bestel opnieuw
            </button>
            <button
              onClick={dismissRepeat}
              data-testid="repeat-order-dismiss"
              aria-label="Verberg vorige bestelling"
              className="w-11 h-11 flex items-center justify-center text-[#0D3B4C] hover:bg-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: dishes + preferences */}
        <div className="lg:col-span-2 space-y-8">
          {/* Dishes grouped by category */}
          {menuLoading ? (
            <div data-testid="menu-skeleton">
              {/* Category nav placeholder — renders immediately */}
              <div className="flex gap-2 mb-8 overflow-hidden">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="dzt-skeleton h-9 w-28 rounded-full flex-shrink-0" />
                ))}
              </div>
              <div className="dzt-skeleton h-8 w-40 rounded mb-5" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="border-2 border-[#E8F5E9] bg-white flex flex-col">
                    <div className="aspect-[4/3] dzt-skeleton" />
                    <div className="p-5 flex flex-col gap-3">
                      <div className="flex justify-between">
                        <div className="dzt-skeleton h-5 w-32 rounded" />
                        <div className="dzt-skeleton h-5 w-14 rounded" />
                      </div>
                      <div className="dzt-skeleton h-4 w-full rounded" />
                      <div className="dzt-skeleton h-4 w-3/4 rounded" />
                      <div className="dzt-skeleton h-11 w-40 rounded-full mx-auto mt-2" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : menuError ? (
            <div className="border-2 border-[#B71C1C] bg-[#FDECEA] p-6 text-center" data-testid="menu-error">
              <p className="font-[700] text-[#B71C1C] mb-3">Het menu kon niet geladen worden. Controleer je verbinding en probeer het opnieuw.</p>
              <button onClick={() => refreshMenu()} data-testid="menu-retry"
                className="bg-[#1B6B2E] text-white font-[700] px-5 py-2.5 rounded-full hover:bg-[#144F22] transition-colors">
                Opnieuw proberen
              </button>
            </div>
          ) : menu.length === 0 ? (
            <p className="font-[600] text-[#0D3B4C]" data-testid="menu-empty">Er staan momenteel geen gerechten online.</p>
          ) : (
          <div>
            {grouped.length > 1 && (
              <div className="sticky top-0 z-20 -mx-6 px-6 py-3 mb-8 bg-[#FBF3E7]/85 backdrop-blur-md border-b border-[#E8D9C3]" data-testid="category-nav">
                <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
                  {grouped.map((c) => (
                    <button key={c.key} onClick={() => scrollToCat(c.key)} data-testid={`cat-nav-${c.key}`}
                      className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-[700] transition-colors ${
                        activeCat === c.key ? "bg-[#7A5230] text-white" : "bg-white text-[#7A5230] border border-[#E8D9C3] hover:bg-[#F1E4D0]"
                      }`}>
                      {c.label} · {c.items.length}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-12">
              {grouped.map((c) => (
                <section key={c.key} id={`cat-${c.key}`} data-testid={`cat-section-${c.key}`} className="scroll-mt-24">
                  {c.image && (
                    <div className="mb-4 rounded-2xl overflow-hidden h-32 md:h-40 relative" data-testid={`cat-banner-${c.key}`}>
                      <img src={c.image.startsWith("http") ? c.image : `${BACKEND}${c.image}`} alt={c.label} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#7A5230]/70 to-transparent" />
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-5 pb-2 border-b-2 border-[#E8D9C3]">
                    <h2 className="text-2xl md:text-3xl font-[700] text-[#7A5230] tracking-tight" data-testid={`cat-heading-${c.key}`}>{c.label}</h2>
                    <span className="text-sm font-[700] text-[#7A5230]/60">{c.items.length}</span>
                    {c.has_schedule && (c.days_abbr?.length > 0 || c.slot_labels?.length > 0) && (
                      <span className="text-xs md:text-sm font-[600] text-[#7A5230]/75" data-testid={`cat-hint-${c.key}`}>
                        · {(c.days_abbr || []).join(", ")}
                        {c.slot_labels?.length > 0 && <> · {(c.slot_labels || []).join(" / ")}</>}
                      </span>
                    )}
                    {c.sold_out && (
                      <span className="ml-auto bg-[#B71C1C] text-white font-[700] uppercase tracking-wider text-xs px-3 py-1.5 rounded-full" data-testid={`cat-soldout-${c.key}`}>
                        Uitverkocht
                      </span>
                    )}
                    {c.deadline_info?.remaining_seconds > 0 ? (
                      <DeadlineCountdown seconds={c.deadline_info.remaining_seconds} label={c.label} testKey={c.key} />
                    ) : c.deadline_info?.has_deadline && c.deadline_info?.next_order_sentence ? (
                      <span className="text-xs font-[600] text-[#7A5230]/75" data-testid={`cat-nextorder-${c.key}`}>
                        {c.deadline_info.next_order_sentence}
                      </span>
                    ) : null}
                  </div>
                  {c.min_order > 0 && (
                    <p className="-mt-3 mb-2 text-xs md:text-sm font-[600] text-[#7A5230]/75" data-testid={`cat-minorder-${c.key}`}>
                      Minimaal {c.min_order} stuks
                    </p>
                  )}
                  {c.description && (
                    <p className="-mt-1 mb-5 text-sm md:text-base font-[400] text-[#5f4a3a] leading-relaxed max-w-[720px]" data-testid={`cat-description-${c.key}`}>
                      {c.description}
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {c.items.map((d) => renderDish(d, c.sold_out))}
                  </div>
                </section>
              ))}
            </div>
          </div>
          )}

          {/* Per-category pickup pickers */}
          {scheduledCats.map((c) => {
            const cp = categoryPickups[c.key] || {};
            const dates = c.pickup_dates || [];
            const dObj = dates.find((d) => d.date === cp.date);
            const slots = dObj?.slots || [];
            return (
              <div key={c.key} className="border-2 border-[#E8D9C3] bg-[#FBF3E7] p-5" data-testid={`cat-pickup-${c.key}`}>
                <h3 className="font-[700] text-[#7A5230] mb-3">Ophalen — {c.label}</h3>
                {c.deadline_info?.has_deadline && dates.length > 0 && (
                  <p className="text-xs font-[600] text-[#7A5230]/75 mb-3" data-testid={`cat-deadline-hint-${c.key}`}>
                    Bestellen tot {c.deadline_info.deadline} voor afhaling op {c.deadline_info.same_day ? "dezelfde dag" : "de volgende dag"}.
                  </p>
                )}
                {dates.length === 0 ? (
                  <p className="text-sm font-[600] text-[#B71C1C]" data-testid={`cat-nopickup-${c.key}`}>
                    {c.deadline_info?.sentence || `Geen ophaaldagen beschikbaar voor ${c.label}.`}
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-3 mb-4">
                      {dates.map((d) => (
                        <button key={d.date} onClick={() => setCatPickup(c.key, { date: d.date, label: d.label, slot: "" })}
                          disabled={d.full} data-testid={`cat-${c.key}-day-${d.date}`}
                          className={`relative px-6 py-3 font-[700] border-2 transition-colors ${
                            d.full ? "bg-[#F1F1F1] text-[#9AA0A6] border-[#E0E0E0] cursor-not-allowed line-through"
                            : cp.date === d.date ? "bg-[#7A5230] text-white border-[#7A5230]"
                            : "bg-white text-[#7A5230] border-[#7A5230] hover:bg-[#F1E4D0]"
                          }`}>
                          {d.label}
                          {d.full && <span className="ml-2 text-xs font-[700] no-underline text-[#B71C1C]">Vol</span>}
                        </button>
                      ))}
                    </div>
                    {cp.date && (
                      <div className="flex flex-wrap gap-3">
                        {slots.map((t) => (
                          <button key={t.slot} onClick={() => setCatPickup(c.key, { slot: t.slot })}
                            disabled={t.full} data-testid={`cat-${c.key}-slot-${t.slot}`}
                            className={`px-5 py-3 font-[700] border-2 transition-colors ${
                              t.full ? "bg-[#F1F1F1] text-[#9AA0A6] border-[#E0E0E0] cursor-not-allowed line-through"
                              : cp.slot === t.slot ? "bg-[#7A5230] text-white border-[#7A5230]"
                              : "bg-white text-[#7A5230] border-[#7A5230] hover:bg-[#F1E4D0]"
                            }`}>
                            {t.slot}
                            {t.full && <span className="ml-2 text-xs font-[700] no-underline text-[#B71C1C]">Vol</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Global pickup (categories without their own schedule) */}
          {hasGlobal && (
          <>
          {/* Pickup day */}
          <div>
            <h3 className="font-[600] text-[#0D3B4C] mb-3">Ophaaldag</h3>
            {pickupDates.length === 0 ? (
              <p className="text-sm font-[600] text-[#B71C1C]" data-testid="no-pickup-dates">
                Er zijn momenteel geen ophaaldagen beschikbaar. Kom snel terug!
              </p>
            ) : (
            <div className="flex flex-wrap gap-3">
              {pickupDates.map((d) => (
                <button
                  key={d.date}
                  onClick={() => update({ pickupDate: d.date, pickupLabel: d.label, timeSlot: "" })}
                  disabled={d.full}
                  data-testid={`day-${d.date}`}
                  className={`relative px-6 py-3 font-[700] border-2 transition-colors ${
                    d.full
                      ? "bg-[#F1F1F1] text-[#9AA0A6] border-[#E0E0E0] cursor-not-allowed line-through"
                      : pickupDate === d.date
                      ? "bg-[#1B6B2E] text-white border-[#1B6B2E]"
                      : "bg-white text-[#0D3B4C] border-[#1B6B2E] hover:bg-[#E8F5E9]"
                  }`}
                >
                  {d.label}
                  {d.full && <span className="ml-2 text-xs font-[700] no-underline text-[#B71C1C]" data-testid={`day-full-${d.date}`}>Vol</span>}
                </button>
              ))}
            </div>
            )}
          </div>

          {/* Time slot */}
          <div>
            <h3 className="font-[600] text-[#0D3B4C] mb-3">Tijdslot</h3>
            {!pickupDate ? (
              <p className="text-sm font-[600] text-[#0D3B4C]" data-testid="slot-hint">Kies eerst een ophaaldag.</p>
            ) : (
            <div className="flex flex-wrap gap-3">
              {timeSlots.map((t) => (
                <button
                  key={t.slot}
                  onClick={() => update({ timeSlot: t.slot })}
                  disabled={t.full}
                  data-testid={`slot-${t.slot}`}
                  className={`px-5 py-3 font-[700] border-2 transition-colors ${
                    t.full
                      ? "bg-[#F1F1F1] text-[#9AA0A6] border-[#E0E0E0] cursor-not-allowed line-through"
                      : timeSlot === t.slot
                      ? "bg-[#1B6B2E] text-white border-[#1B6B2E]"
                      : "bg-white text-[#0D3B4C] border-[#1B6B2E] hover:bg-[#E8F5E9]"
                  }`}
                >
                  {t.slot}
                  {t.full && <span className="ml-2 text-xs font-[700] no-underline text-[#B71C1C]">Vol</span>}
                </button>
              ))}
            </div>
            )}
          </div>
          </>
          )}
        </div>

        {/* Right: sticky summary */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-6 bg-[#E8F5E9] p-6" data-testid="order-summary">
            <h3 className="text-xl font-[700] text-[#0D3B4C] mb-4">Overzicht</h3>
            {selected.length === 0 ? (
              <p className="font-[400] text-[#0D3B4C]">Nog geen gerechten geselecteerd.</p>
            ) : (
              <div className="space-y-2">
                {selected.map((d) => (
                  <div key={d.id} className="flex justify-between text-sm font-[600] text-[#0D3B4C]">
                    <span>{d.name} × {quantities[d.id]}</span>
                    <span>{formatEuro(quantities[d.id] * d.price)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t-2 border-white my-4" />
            <div className="flex justify-between font-[600] text-[#0D3B4C]">
              <span>Totaal porties (personen)</span>
              <span data-testid="summary-portions">{totalPortions}</span>
            </div>
            <div className="flex justify-between text-2xl font-[700] text-[#0D3B4C] mt-3">
              <span>Totaal</span>
              <span data-testid="summary-total">{formatEuro(total)}</span>
            </div>
            <p className="text-sm font-[400] text-[#0D3B4C] mt-1">waarvan 9% BTW: {formatEuro(vat)}</p>

            {unmetCats.length > 0 && (
              <div className="mt-4 space-y-1" data-testid="category-min-warning">
                {unmetCats.map((c) => {
                  const need = c.min_order - (catPortionMap[c.key] || 0);
                  return (
                    <p key={c.key} className="text-sm font-[600] text-[#B71C1C]">
                      Je hebt minimaal {c.min_order} {c.label} items nodig om door te gaan met bestellen. Voeg nog {need} {c.label} item{need !== 1 ? "s" : ""} toe.
                    </p>
                  );
                })}
              </div>
            )}
            <button
              onClick={goToCheckout}
              disabled={!canProceed}
              data-testid="proceed-checkout"
              className="w-full mt-6 bg-[#B71C1C] text-white font-[700] uppercase tracking-wider py-4 hover:bg-[#8E1515] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Verder naar bestellen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
